import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { Download, Plus } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useAuth } from "@/hooks/useAuth";
import { providerApiFor } from "@/api/serviceProviders";
import { useProviderList } from "@/hooks/capitalMarkets/useServiceProviders";
import {
  useEntityNameMap,
  usePersonNameMap,
} from "@/hooks/capitalMarkets/useCounterpartyNames";
import type { AnyServiceProviderDto } from "@/types/serviceProviders";
import { ProviderStatusBadge } from "../shared/components";
import { downloadCsv, formatDate, humanizeStatus } from "../shared/format";
import type { ProviderConfig } from "./providerConfig";
import { NewProviderModal } from "./NewProviderModal";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

/** Extracts the type-specific "engagement" date used as the list column. */
function engagementDate(p: AnyServiceProviderDto): string | null {
  if ("apaAgreementDate" in p) return p.apaAgreementDate;
  if ("appointmentDate" in p) return p.appointmentDate;
  if ("agreementDate" in p) return p.agreementDate;
  return null;
}

export function ServiceProviderListView({ config }: { config: ProviderConfig }) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const tz = useUserTimeZone();
  const canManage = hasPermission(config.managePermission);

  const [statusFilter, setStatusFilter] = useState("");
  const [showNew, setShowNew] = useState(false);

  const listQuery = useProviderList(config.type, { pageSize: 200 });
  const { map: entityNames } = useEntityNameMap();
  const { map: personNames } = usePersonNameMap();

  const providers = useMemo(
    () => (listQuery.data?.data ?? []) as AnyServiceProviderDto[],
    [listQuery.data],
  );

  // # Pools Served per provider — one pools-served fetch per row. Provider
  // counts are small for an internal tool, so the N requests are acceptable.
  const poolsServedQueries = useQueries({
    queries: providers.map((p) => ({
      queryKey: ["service-providers", config.type, "pools-served", p.id],
      queryFn: () => providerApiFor(config.type).poolsServed(p.id),
    })),
  });
  const poolsServedCount = useMemo(() => {
    const m = new Map<string, number>();
    providers.forEach((p, i) => {
      m.set(p.id, poolsServedQueries[i]?.data?.length ?? 0);
    });
    return m;
  }, [providers, poolsServedQueries]);

  const rows = useMemo(
    () =>
      statusFilter
        ? providers.filter((p) => p.status === statusFilter)
        : providers,
    [providers, statusFilter],
  );

  const nameOf = (p: AnyServiceProviderDto) =>
    entityNames.get(p.entityId) ?? "Unnamed entity";
  const contactOf = (p: AnyServiceProviderDto) =>
    p.contactPersonId ? (personNames.get(p.contactPersonId) ?? "—") : "—";

  const columns: Column<AnyServiceProviderDto>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        render: (p) => (
          <Link
            to={`${config.routeBase}/${p.id}`}
            className="font-semibold hover:text-gold-shadow"
          >
            {nameOf(p)}
          </Link>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (p) => <ProviderStatusBadge status={p.status} />,
      },
      {
        key: "pools",
        header: "# Pools Served",
        render: (p) => poolsServedCount.get(p.id) ?? 0,
      },
      {
        key: "engagement",
        header: "Engagement Date",
        render: (p) => formatDate(engagementDate(p)),
      },
      {
        key: "contact",
        header: "Primary Contact",
        render: (p) => contactOf(p),
      },
      {
        key: "actions",
        header: "",
        className: "w-10 text-right",
        render: (p) => (
          <Link
            to={`${config.routeBase}/${p.id}`}
            className="text-caption text-gold-shadow font-display uppercase tracking-wide hover:underline"
          >
            View
          </Link>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.routeBase, poolsServedCount, entityNames, personNames],
  );

  const handleExport = () => {
    const header = [
      "Name",
      "Status",
      "# Pools Served",
      "Engagement Date",
      "Primary Contact",
    ];
    const csv = rows.map((p) => [
      nameOf(p),
      humanizeStatus(p.status),
      String(poolsServedCount.get(p.id) ?? 0),
      engagementDate(p) ?? "",
      contactOf(p),
    ]);
    downloadCsv(
      `${config.type}-${isoDateInTz(new Date(), tz)}.csv`,
      [header, ...csv],
    );
  };

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex flex-wrap items-center justify-between gap-space-3">
        <div className="flex items-center gap-space-2">
          <Select
            aria-label="Filter by status"
            className="w-44"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            {config.statuses.map((s) => (
              <option key={s} value={s}>
                {humanizeStatus(s)}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-space-3">
          <Button variant="secondary" size="sm" onClick={handleExport}>
            <Download size={14} strokeWidth={1.5} aria-hidden /> Export CSV
          </Button>
          {canManage && (
            <Button onClick={() => setShowNew(true)}>
              <Plus size={16} strokeWidth={2} aria-hidden /> New{" "}
              {config.singularLabel}
            </Button>
          )}
        </div>
      </div>

      <div
        role="presentation"
        onClick={(e) => {
          const tr = (e.target as HTMLElement).closest("tr[data-provider-id]");
          if (tr)
            navigate(`${config.routeBase}/${tr.getAttribute("data-provider-id")}`);
        }}
      >
        <DataTable
          columns={columns}
          rows={rows}
          loading={listQuery.isLoading}
          error={
            listQuery.isError
              ? `Could not load ${config.pluralLabel.toLowerCase()}.`
              : null
          }
          emptyMessage={`No ${config.pluralLabel.toLowerCase()} yet. Create your first record to begin.`}
          rowKey={(p) => p.id}
        />
      </div>

      {showNew && (
        <NewProviderModal
          config={config}
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            navigate(`${config.routeBase}/${id}`);
          }}
        />
      )}
    </div>
  );
}
